import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createUploadLink } from 'apollo-upload-client';
import { ACCESS_TOKEN_HEADER_NAME } from 'clerk_common/constants';
import { Client, createClient } from 'graphql-ws';
import { waitForGraphQLWebSocketRetry } from './graphqlWebSocket';

const GRAPHQL_WEBSOCKET_KEEP_ALIVE_MS = 20_000;
const GRAPHQL_WEBSOCKET_RETRY_ATTEMPTS = 20;
const APOLLO_REQUIRE_PREFLIGHT_HEADER = 'Apollo-Require-Preflight';

type CreateApolloClientArgs = {
  token?: string;
  appName: string;
  httpLinkUri: string;
  webSocketLinkUri?: string;
};

export type ApolloClientResources = {
  client: ApolloClient<NormalizedCacheObject>;
  dispose: () => Promise<void>;
};

export const createApolloClientResources = (
  args: CreateApolloClientArgs
): ApolloClientResources => {
  const httpLink = createUploadLink({
    headers: {
      [APOLLO_REQUIRE_PREFLIGHT_HEADER]: 'true',
    },
    uri: args.httpLinkUri,
  });

  const authLink = setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        ...getAccessTokenHeaders(args.token),
      },
    };
  });

  let webSocketClient: Client | undefined;
  // NOTE(mike): The upload client's types resolve a separate Apollo version.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  let link: ApolloLink = authLink.concat(httpLink);
  if (args.webSocketLinkUri) {
    webSocketClient = createClient({
      connectionParams: () => getAccessTokenHeaders(args.token),
      keepAlive: GRAPHQL_WEBSOCKET_KEEP_ALIVE_MS,
      lazy: true,
      retryAttempts: GRAPHQL_WEBSOCKET_RETRY_ATTEMPTS,
      retryWait: waitForGraphQLWebSocketRetry,
      url: args.webSocketLinkUri,
    });
    link = ApolloLink.split(
      isSubscriptionOperation,
      new GraphQLWsLink(webSocketClient),
      link
    );
  }

  const client = new ApolloClient({
    link,
    cache: new InMemoryCache(),
    clientAwareness: {
      name: args.appName,
    },
  });

  return {
    client,
    dispose: async () => {
      client.stop();
      await webSocketClient?.dispose();
    },
  };
};

export const createApolloClient = (
  args: CreateApolloClientArgs
): ApolloClient<NormalizedCacheObject> =>
  createApolloClientResources(args).client;

export function getAccessTokenHeaders(token?: string): Record<string, string> {
  return token ? { [ACCESS_TOKEN_HEADER_NAME]: token } : {};
}

export function getGraphQLWebSocketUri(httpLinkUri: string): string {
  const url = new URL(httpLinkUri);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.pathname = '/api';
  url.search = '';
  url.hash = '';
  return url.toString();
}

export function isSubscriptionOperation({
  query,
}: {
  query: Parameters<typeof getMainDefinition>[0];
}): boolean {
  const definition = getMainDefinition(query);
  return (
    definition.kind === 'OperationDefinition' &&
    definition.operation === 'subscription'
  );
}
